import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi, useAuth } from '../../hooks';
import type {
  CareBlockFeedbackSubmission,
  CareTeamFeedbackActivity,
} from '../../models/careBlockDetail';
import { careBlockDetailKey } from './useCareBlockDetail';

/**
 * Submits patient-reported feedback on one care block
 * (POST /private/care-block/:blockId/feedback).
 *
 * On success it invalidates only that block's detail query, so the new entry
 * appears at the top of the feed without disturbing any other open data.
 */
export const useSubmitCareBlockFeedback = (blockId: string | null) => {
  const queryClient = useQueryClient();
  const { api } = useApi({ throwIfError: true });
  const { userToken } = useAuth();

  return useMutation<
    CareTeamFeedbackActivity,
    Error,
    CareBlockFeedbackSubmission
  >({
    mutationFn: (submission) =>
      api({
        route: `/private/care-block/${blockId}/feedback`,
        method: 'POST',
        payload: submission,
        token: userToken,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: careBlockDetailKey(blockId ?? '', userToken),
      });
    },
  });
};
